import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, FileDown, Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";

/* Admin — Caregiver Tools manager for the "30 Days" series.
 * Add a day, upload its PDF, toggle Published — no rebuild needed. */

const EMPTY = {
    id: null, day: 1, slug: "", title: "", short_description: "", includes: [],
    digital_url: "/journey/about", social_title: "", social_description: "",
    social_image: "", thumbnail: "", published: false,
};

export default function CaregiverToolsView() {
    const [tools, setTools] = useState(null);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState("");

    const load = () => api.get("/admin/caregiver-tools").then(r => setTools(r.data.tools || [])).catch(() => setTools([]));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title is required."); return; }
        setBusy("save");
        try {
            const payload = { ...form, day: Number(form.day) || 1, slug: form.slug || form.title };
            const r = await api.post("/admin/caregiver-tools", payload);
            toast.success("Saved.");
            setForm(r.data.tool.id ? { ...form, id: r.data.tool.id, slug: r.data.tool.slug } : form);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
        finally { setBusy(""); }
    };

    const togglePublish = async (t) => {
        try {
            await api.post("/admin/caregiver-tools", { ...t, published: !t.published });
            load();
            toast.success(!t.published ? `Day ${t.day} is now live.` : `Day ${t.day} unpublished.`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete Day ${t.day} — ${t.title}? This cannot be undone.`)) return;
        await api.delete(`/admin/caregiver-tools/${t.id}`);
        load();
    };

    if (tools === null) return <div className="flex items-center gap-2 text-[#6E7A74] p-6"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

    return (
        <div className="space-y-5" data-testid="admin-caregiver-tools">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-lg font-semibold text-[#1F2624]">Caregiver Tools — 30 Days series</h2>
                    <p className="text-sm text-[#6E7A74]">Public library at <a href="/caregiver-tools" target="_blank" rel="noreferrer" className="text-teal-dark underline">/caregiver-tools</a>. Only published tools appear to visitors.</p>
                </div>
                <button className="ml-btn-primary !min-h-[40px]" data-testid="admin-tool-new"
                    onClick={() => setForm({ ...EMPTY, day: (Math.max(0, ...tools.map(t => t.day)) + 1) })}>
                    <Plus size={16} /> New tool
                </button>
            </div>

            <div className="ml-card divide-y divide-cream-dark">
                {tools.length === 0 && <p className="p-5 text-sm text-[#6E7A74]">No tools yet.</p>}
                {tools.map(t => (
                    <div key={t.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-tool-row-${t.slug}`}>
                        <span className="rounded-full bg-teal-dark text-white text-[11px] font-semibold px-2.5 py-1">DAY {t.day}</span>
                        <div className="flex-1 min-w-[180px]">
                            <p className="text-sm font-semibold text-[#1F2624]">{t.title}</p>
                            <p className="text-xs text-[#6E7A74]">/caregiver-tools/{t.slug} · {t.downloads || 0} downloads · {t.views || 0} views{!t.pdf_source && !t.published ? "" : !t.pdf_source ? " · NO PDF" : ""}</p>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-[#4C5551] cursor-pointer" data-testid={`admin-tool-publish-${t.slug}`}>
                            <input type="checkbox" checked={!!t.published} onChange={() => togglePublish(t)} className="accent-teal-dark" />
                            Published
                        </label>
                        <a href={`/caregiver-tools/${t.slug}`} target="_blank" rel="noreferrer" className="text-[#6E7A74] hover:text-teal-dark"><ExternalLink size={15} /></a>
                        <button onClick={() => setForm({ ...EMPTY, ...t })} className="text-[#6E7A74] hover:text-teal-dark" data-testid={`admin-tool-edit-${t.slug}`}><Pencil size={15} /></button>
                        <button onClick={() => remove(t)} className="text-[#6E7A74] hover:text-coral-dark"><Trash2 size={15} /></button>
                    </div>
                ))}
            </div>

            {form && <ToolForm form={form} setForm={setForm} save={save} busy={busy} onUploaded={load} />}
        </div>
    );
}

function ToolForm({ form, setForm, save, busy, onUploaded }) {
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
    const pdfRef = useRef(); const thumbRef = useRef(); const socialRef = useRef();
    const [uploading, setUploading] = useState("");

    const upload = async (kind, input) => {
        const file = input.current?.files?.[0];
        if (!file) return;
        if (!form.id) { toast.error("Save the tool first, then upload files."); return; }
        setUploading(kind);
        try {
            const fd = new FormData();
            fd.append("kind", kind);
            fd.append("file", file);
            await api.post(`/admin/caregiver-tools/${form.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${kind === "pdf" ? "PDF" : kind} uploaded.`);
            onUploaded();
        } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
        finally { setUploading(""); if (input.current) input.current.value = ""; }
    };

    const field = (label, k, props = {}) => (
        <label className="block">
            <span className="text-xs font-semibold text-[#6E7A74]">{label}</span>
            <input value={form[k] || ""} onChange={set(k)} {...props}
                data-testid={`admin-tool-field-${k}`}
                className="mt-1 w-full rounded-lg border border-cream-dark px-3 py-2 text-sm" />
        </label>
    );

    return (
        <div className="ml-card p-5 space-y-3" data-testid="admin-tool-form">
            <h3 className="font-heading font-semibold text-[#1F2624]">{form.id ? `Edit — Day ${form.day}` : "New tool"}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field("Day number", "day", { type: "number", min: 1, max: 60 })}
                {field("Title", "title", { placeholder: "Your Person at a Glance" })}
                {field("Slug (URL)", "slug", { placeholder: "person-at-a-glance" })}
            </div>
            <label className="block">
                <span className="text-xs font-semibold text-[#6E7A74]">Short description</span>
                <textarea value={form.short_description || ""} onChange={set("short_description")} rows={2}
                    data-testid="admin-tool-field-short_description"
                    className="mt-1 w-full rounded-lg border border-cream-dark px-3 py-2 text-sm" />
            </label>
            <label className="block">
                <span className="text-xs font-semibold text-[#6E7A74]">What the printable covers (one item per line)</span>
                <textarea value={(form.includes || []).join("\n")} rows={4}
                    onChange={(e) => setForm(f => ({ ...f, includes: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) }))}
                    data-testid="admin-tool-field-includes"
                    className="mt-1 w-full rounded-lg border border-cream-dark px-3 py-2 text-sm" />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field("Digital Memory Lane destination", "digital_url", { placeholder: "/journey/about" })}
                {field("Social title", "social_title", { placeholder: "Day 2: … | Free Dementia Caregiver Tool" })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field("Social description", "social_description")}
                {field("Social image URL (or upload below)", "social_image", { placeholder: "/og/…png" })}
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-1">
                {[["pdf", "Upload PDF", pdfRef, ".pdf"], ["thumbnail", "Upload thumbnail", thumbRef, "image/png,image/jpeg"], ["social", "Upload social image", socialRef, "image/png,image/jpeg"]].map(([kind, label, ref, accept]) => (
                    <span key={kind} className="inline-flex items-center gap-1.5">
                        <input type="file" ref={ref} accept={accept} className="hidden" onChange={() => upload(kind, ref)} data-testid={`admin-tool-upload-${kind}`} />
                        <button onClick={() => ref.current?.click()} disabled={uploading === kind}
                            className="ml-btn-secondary !min-h-[36px] !px-3.5 text-xs">
                            {uploading === kind ? <Loader2 size={13} className="animate-spin" /> : kind === "pdf" ? <FileDown size={13} /> : <Upload size={13} />} {label}
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-3 pt-1">
                <label className="flex items-center gap-1.5 text-sm text-[#4C5551]">
                    <input type="checkbox" checked={!!form.published} onChange={(e) => setForm(f => ({ ...f, published: e.target.checked }))} className="accent-teal-dark" data-testid="admin-tool-field-published" />
                    Published (visible to the public)
                </label>
                <button onClick={save} disabled={busy === "save"} className="ml-btn-primary !min-h-[40px]" data-testid="admin-tool-save">
                    {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : null} Save tool
                </button>
                <button onClick={() => setForm(null)} className="text-sm text-[#6E7A74] hover:text-[#1F2624]">Close</button>
            </div>
        </div>
    );
}
